import React, { useState } from "react";
import BaseListPortalView from "../../overlay/BaseListPortalView";
import { useLayout } from "../../layout/LayoutContext";
import { OverlayOpacity } from "../../portal/types";
import styles from "./FamilyProfileOverlaySheet.module.css";
import ActionButton from "../../button/ActionButton";
import InboxPortalView from "../../inbox/InboxPortalView";
import { Connection, ParticipantSummary, UserSummary } from "../../../gql/types";
import noUser from "../../../assets/no-user.png";
import PlaydateInviteOrganizerCreationWorkflow from "../../playdate/workflow/PlaydateInviteOrganizerCreationWorkflow";
import FamilyMemberList from "./FamilyMemberList";
import { BackgroundColorType } from "../../overlay/types";
import { useToastContext } from "contexts/ToastContext";
import { showToast } from "toast/toast";

type FamilyProfileOverlaySheetProps = {
    onBack: () => void;
    connection: Connection;

    baseZIndex?: number;
    // Custom Action Button
    onContinue?: () => void;
    continueButtonText?: string;

    overlayOpacity?: OverlayOpacity;
};

const FamilyProfileOverlaySheet: React.FC<FamilyProfileOverlaySheetProps> = ({
    onBack,
    baseZIndex,
    connection,
    onContinue,
    continueButtonText,
    overlayOpacity = OverlayOpacity.GRAY,
}) => {
    const [showInviteCreationView, setShowInviteCreatonView] = useState(false);
    const [showInbox, setShowInbox] = useState(false);
    const { baseZIndex: layoutZIndex } = useLayout();
    const { triggerToastEvent } = useToastContext();

    if (!baseZIndex) {
        baseZIndex = layoutZIndex;
    }

    const isUser = (p: ParticipantSummary): p is UserSummary => "email" in p;

    const displayName = isUser(connection.participant)
        ? `${connection.participant.firstName || ""} ${connection.participant.lastName || ""}`.trim()
        : connection.participant.name;

    const profilePictureUrl = isUser(connection.participant)
        ? connection.participant.profilePictureUrl
        : connection.participant.parents?.[0]?.profilePictureUrl;

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.familyImageContainer}>
                <img src={profilePictureUrl ?? noUser} className={styles.familyImage} />
            </div>
            <div className={styles.headerNameContainer}>
                <div className={styles.headerName}>
                    {displayName}
                </div>
            </div>
        </div>
    );

    const family = connection.participant.__typename === "FamilySummary"
        ? connection.participant
        : null;

    const handleCreateInvite = () => {
        setShowInviteCreatonView(true);
    };

    const handleShowInbox = () => {
        setShowInbox(true);
    };

    const footer = (
        <>
            {onContinue
                ? <div className={styles.footer}>
                    <ActionButton
                        onClick={onContinue}
                        colorPattern={"Default"}
                        className={styles.button}
                    >
                        {continueButtonText}
                    </ActionButton>
                </div>
                : <div className={styles.footer}>
                    <ActionButton
                        onClick={handleShowInbox}
                        colorPattern={"Transparent"}
                        className={styles.button}
                    >
                        View Playdate History
                    </ActionButton>
                    <ActionButton
                        onClick={handleCreateInvite}
                        colorPattern={"Default"}
                        className={styles.button}
                    >
                        Create a New Invite
                    </ActionButton>
                </div >
            }
        </>
    );

    return (
        <BaseListPortalView
            onBack={onBack}
            header={header}
            footer={footer}
            baseZIndex={baseZIndex}
            overlayOpacity={overlayOpacity}
            sheetClassName={styles.sheet}
            backgroundColor={BackgroundColorType.PURPLE}
        >
            <div className={styles.familyProfileContainer}>
                {/* Member List */}
                <div className={styles.content}>
                    {family && <FamilyMemberList family={family} />}
                </div>
                {showInviteCreationView && (
                    <PlaydateInviteOrganizerCreationWorkflow
                        onBack={() => setShowInviteCreatonView(false)}
                        initialConnections={[connection.participant]}
                        featureParameters={{
                            connectionIDs: [connection.connectionID],
                        }}
                        onDataChange={(success, _updatedInvite) => {
                            if (success) {
                                triggerToastEvent();
                                setShowInviteCreatonView(false);
                            }
                        }}
                    />
                )}
                {showInbox && (
                    <InboxPortalView
                        onCloseInbox={() => setShowInbox(false)}
                        target={connection.connectionID}
                        targetConnection={connection}
                    />
                )}
            </div>
        </BaseListPortalView>
    );
};

export default FamilyProfileOverlaySheet;
